import { useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import { Panel, PanelContent, PanelHeader, PanelTitle } from '@multaqa/ui/components/ui/panel'
import { PencilIcon } from 'lucide-react'
import type { ReactNode } from 'react'

export function SettingsCard({
  title,
  description,
  editing,
  pending,
  error,
  message,
  readOnly = false,
  onEdit,
  onCancel,
  onSave,
  children,
  testId,
}: {
  title: string
  description?: string
  editing: boolean
  pending: boolean
  error: boolean
  message?: string | null
  readOnly?: boolean
  onEdit: () => void
  onCancel: () => void
  onSave: () => void
  children: ReactNode
  testId?: string
}) {
  const { t } = useTranslation()
  return (
    <Panel data-testid={testId}>
      <PanelHeader>
        <div>
          <PanelTitle>{title}</PanelTitle>
          {description ? (
            <p className="text-small text-muted-foreground mt-0.5">{description}</p>
          ) : null}
        </div>
        {!readOnly && editing ? (
          <div className="ms-auto flex gap-2">
            <Button type="button" variant="outline" size="sm" disabled={pending} onClick={onCancel}>
              {t('action.cancel')}
            </Button>
            <Button
              type="button"
              size="sm"
              loading={pending}
              disabled={pending}
              onClick={onSave}
              data-testid={testId ? `${testId}-save` : undefined}
            >
              {t('companies.settings.form.save')}
            </Button>
          </div>
        ) : !readOnly ? (
          <Button type="button" variant="outline" size="sm" className="ms-auto" onClick={onEdit}>
            <PencilIcon className="size-3.5" aria-hidden="true" />
            {t('companies.settings.form.edit')}
          </Button>
        ) : null}
      </PanelHeader>
      <PanelContent className="p-4">
        {error || message ? (
          <p className="mb-3 text-small text-danger" role="alert">
            {message ?? t('companies.settings.form.saveFailed')}
          </p>
        ) : null}
        {children}
      </PanelContent>
    </Panel>
  )
}
